<app-page-breadcrumb pageTitle="Conexión de correo (SMTP/IMAP)"></app-page-breadcrumb>

<div class="space-y-6">

  <!-- Error inline (se mantiene fijo mientras el usuario corrige; el toast es transitorio) -->
  @if (error) {
  <div class="p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
    <div class="flex items-center gap-3">
      <svg class="w-5 h-5 text-error-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <p class="text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  </div>
  }

  <!-- Loading -->
  @if (loading) {
  <div class="flex justify-center items-center py-16">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
    <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando conexión...</span>
  </div>
  }

  @if (!loading) {

  <!-- Estado de la conexión existente -->
  @if (existing) {
  <div class="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-success-50 dark:bg-success-500/10">
          <svg class="w-5 h-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ existing.mailAddress }}</p>
          <div class="mt-0.5 flex flex-wrap items-center gap-2">
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                  [class.bg-success-50]="existing.connectionStatus === 'CONNECTED'"
                  [class.text-success-700]="existing.connectionStatus === 'CONNECTED'"
                  [class.dark:bg-success-500/10]="existing.connectionStatus === 'CONNECTED'"
                  [class.dark:text-success-400]="existing.connectionStatus === 'CONNECTED'"
                  [class.bg-gray-100]="existing.connectionStatus !== 'CONNECTED'"
                  [class.text-gray-600]="existing.connectionStatus !== 'CONNECTED'"
                  [class.dark:bg-gray-800]="existing.connectionStatus !== 'CONNECTED'"
                  [class.dark:text-gray-400]="existing.connectionStatus !== 'CONNECTED'">
              {{ existing.connectionStatus === 'CONNECTED' ? 'Conectada' : existing.connectionStatus }}
            </span>
            @if (imapConfigurado) {
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400">
              Envío + Lectura (IMAP)
            </span>
            } @else {
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-warning-50 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400">
              Solo envío — sin IMAP
            </span>
            }
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
        @if (imapConfigurado) {
        <app-button variant="outline" size="sm" type="button" [disabled]="testing" (btnClick)="probarBandeja()">
          {{ testing ? 'Probando...' : 'Probar bandeja' }}
        </app-button>
        <app-button variant="outline" size="sm" type="button" (btnClick)="irABandeja()">
          Ir a la bandeja
        </app-button>
        }
        <ng-container *appHasPermission="'mail:delete'">
          <app-button variant="danger" size="sm" type="button" [disabled]="deleting" (btnClick)="eliminar()">
            {{ deleting ? 'Eliminando...' : 'Eliminar conexión' }}
          </app-button>
        </ng-container>
      </div>
    </div>
  </div>
  }

  <app-component-card
    [title]="existing ? 'Editar conexión de correo' : 'Registrar conexión de correo'"
    desc="Conecta tu propio servidor de correo. El envío usa SMTP; la lectura de tu bandeja de entrada usa IMAP con las mismas credenciales.">

    <!-- Prellenado rápido -->
    <div class="mb-5 flex flex-wrap items-center gap-2">
      <span class="text-sm text-gray-500 dark:text-gray-400">Prellenar servidores:</span>
      <button
        type="button"
        (click)="prellenarGmail()"
        class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.03]">
        Gmail
      </button>
      <button
        type="button"
        (click)="prellenarOutlook()"
        class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.03]">
        Outlook
      </button>
    </div>

    <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-5">

      <!-- ─── Sección: Envío (SMTP) ─────────────────────────────────── -->
      <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
        <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">Envío (SMTP)</p>

        <!-- Correo -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Correo electrónico <span class="text-error-500">*</span>
          </label>
          <input
            type="email"
            formControlName="mailAddress"
            placeholder="usuario@gmail.com"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            [class.border-error-300]="hasError('mailAddress')"
            [class.border-gray-300]="!hasError('mailAddress')"
            [class.dark:border-gray-700]="!hasError('mailAddress')"
          />
          @if (hasError('mailAddress')) {
          <p class="mt-1 text-xs text-error-500">{{ getError('mailAddress') }}</p>
          }
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <!-- Servidor SMTP -->
          <div class="sm:col-span-2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Servidor SMTP <span class="text-error-500">*</span>
            </label>
            <input
              type="text"
              formControlName="host"
              placeholder="smtp.gmail.com"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-300]="hasError('host')"
              [class.border-gray-300]="!hasError('host')"
              [class.dark:border-gray-700]="!hasError('host')"
            />
            @if (hasError('host')) {
            <p class="mt-1 text-xs text-error-500">{{ getError('host') }}</p>
            }
          </div>

          <!-- Puerto -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Puerto <span class="text-error-500">*</span>
            </label>
            <input
              type="number"
              formControlName="port"
              min="1"
              max="65535"
              placeholder="587"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-300]="hasError('port')"
              [class.border-gray-300]="!hasError('port')"
              [class.dark:border-gray-700]="!hasError('port')"
            />
            @if (hasError('port')) {
            <p class="mt-1 text-xs text-error-500">{{ getError('port') }}</p>
            } @else {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Típico: 587 (TLS) o 465 (SSL)</p>
            }
          </div>
        </div>

        <!-- TLS / SSL -->
        <div class="flex items-center gap-6">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              formControlName="useTls"
              class="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
            />
            <span class="text-sm text-gray-700 dark:text-gray-300">Usar TLS</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              formControlName="useSsl"
              class="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
            />
            <span class="text-sm text-gray-700 dark:text-gray-300">Usar SSL</span>
          </label>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <!-- Usuario -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Usuario <span class="ml-1 text-xs text-gray-400 dark:text-gray-500">(opcional)</span>
            </label>
            <input
              type="text"
              formControlName="username"
              placeholder="Si se deja vacío, se usa el correo"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
          </div>

          <!-- Contraseña -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Contraseña de aplicación <span class="text-error-500">*</span>
            </label>
            <input
              type="password"
              formControlName="password"
              autocomplete="new-password"
              [placeholder]="isEditing ? '••••••••  (sin cambios)' : '••••••••'"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-300]="hasError('password')"
              [class.border-gray-300]="!hasError('password')"
              [class.dark:border-gray-700]="!hasError('password')"
            />
            @if (hasError('password')) {
            <p class="mt-1 text-xs text-error-500">{{ getError('password') }}</p>
            } @else if (isEditing) {
            <p class="mt-1 text-xs text-warning-600 dark:text-warning-400">
              Por seguridad la contraseña no se muestra. Vuelve a ingresar tu contraseña para guardar cambios.
            </p>
            } @else {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Gmail/Outlook requieren una contraseña de aplicación, no la de la cuenta.
            </p>
            }
          </div>
        </div>
      </div>

      <!-- ─── Sección: Lectura (IMAP) ───────────────────────────────── -->
      <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
        <div>
          <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">Lectura (IMAP)</p>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Opcional — necesario para ver tu bandeja de entrada. Se usan las mismas credenciales que SMTP.
            Gmail/Outlook requieren una contraseña de aplicación.
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <!-- Servidor IMAP -->
          <div class="sm:col-span-2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Servidor IMAP
            </label>
            <input
              type="text"
              formControlName="imapHost"
              placeholder="imap.gmail.com"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Si se deja vacío, la conexión solo podrá enviar correos.</p>
          </div>

          <!-- Puerto IMAP -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Puerto IMAP
            </label>
            <input
              type="number"
              formControlName="imapPort"
              min="1"
              max="65535"
              placeholder="993"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-300]="hasError('imapPort')"
              [class.border-gray-300]="!hasError('imapPort')"
              [class.dark:border-gray-700]="!hasError('imapPort')"
            />
            @if (hasError('imapPort')) {
            <p class="mt-1 text-xs text-error-500">{{ getError('imapPort') }}</p>
            } @else {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Típico: 993 (SSL)</p>
            }
          </div>
        </div>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            formControlName="imapUseSsl"
            class="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
          />
          <span class="text-sm text-gray-700 dark:text-gray-300">Usar SSL (993/imaps) — desmarcado usa STARTTLS</span>
        </label>
      </div>

      <!-- Acciones -->
      <div class="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        @if (existing && !imapConfigurado) {
        <p class="mr-auto text-xs text-gray-500 dark:text-gray-400">
          Completa el servidor IMAP y guarda para poder probar la bandeja.
        </p>
        }
        @if (existing && imapConfigurado) {
        <app-button
          variant="outline"
          type="button"
          [disabled]="testing"
          (btnClick)="probarBandeja()">
          {{ testing ? 'Probando bandeja...' : 'Probar bandeja' }}
        </app-button>
        }
        <app-button
          variant="primary"
          type="submit"
          [disabled]="saving">
          {{ saving ? 'Guardando...' : (existing ? 'Guardar cambios' : 'Registrar conexión') }}
        </app-button>
      </div>
    </form>
  </app-component-card>
  }
</div>
